import { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Small overlay/button for admins to upload or replace an artist's profile
 * picture directly from the ArtistDetailPage header (no intake required).
 * Uses the existing Supabase signed-upload pattern via the backend endpoint
 *   POST /api/admin/artists/{id}/pfp/upload-url
 * then PATCH /api/admin/artists/{id}/pfp with the resulting public URL.
 */
export default function ArtistPFPUploader({ artistId, currentUrl, onUpdated, size = 'md' }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const dims = size === 'lg' ? 'w-24 h-24' : 'w-20 h-20';

  const onFile = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error('Image too large (max 15MB)');
      return;
    }
    setUploading(true);
    const t = toast.loading('Uploading…');
    try {
      // 1) Get signed URL from backend
      const signedRes = await axios.post(
        `${API}/admin/artists/${artistId}/pfp/upload-url`,
        { file_name: file.name, mime_type: file.type, kind: 'pfp' },
      );
      const { signed_url, path, public_url, bucket_name } = signedRes.data;

      // 2) Direct PUT to Supabase
      const clean = axios.create();
      await clean.put(signed_url, file, {
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
          'x-upsert': 'true',
        },
      });

      // 3) Commit to artist_profiles row
      const patchRes = await axios.patch(`${API}/admin/artists/${artistId}/pfp`, {
        pfp_url: public_url,
        pfp_storage_path: path,
        pfp_bucket_name: bucket_name,
      });

      toast.success('Profile picture updated', { id: t });
      if (onUpdated) onUpdated(patchRes.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed', { id: t });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={`relative ${dims} shrink-0 group`} data-testid="artist-pfp-uploader">
      <div className="w-full h-full border border-[#222] bg-[#070707] overflow-hidden flex items-center justify-center">
        {currentUrl ? (
          <img
            src={currentUrl}
            alt=""
            className="w-full h-full object-cover"
          />
        ) : (
          <span className="font-outfit text-2xl font-semibold text-[#90E0EF]">
            ?
          </span>
        )}
      </div>

      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        data-testid="artist-pfp-change-btn"
        aria-label={currentUrl ? 'Change profile picture' : 'Upload profile picture'}
        className={`absolute inset-0 flex flex-col items-center justify-center gap-1 bg-[#050505]/85 text-[#f6f6f6] transition-opacity duration-200 ${uploading ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
      >
        {uploading ? (
          <Loader2 className="w-4 h-4 animate-spin text-[#90E0EF]" />
        ) : (
          <>
            <Camera className="w-4 h-4 text-[#90E0EF]" />
            <span className="text-[9px] font-mono uppercase tracking-[0.18em] text-[#f6f6f6]">
              {currentUrl ? 'Change' : 'Upload'}
            </span>
          </>
        )}
      </button>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid="artist-pfp-file-input"
        onChange={(e) => onFile(e.target.files?.[0])}
      />
    </div>
  );
}
